import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import Network from '../../models/Network.js';
import User from '../../models/User.js';
import DemoModeContext from '../../models/DemoModeContext.js';
import URLS from '../../urls.js';
import './Header.css';
const ErrorCodes = require('../../error-codes/error-codes.js')

// exact match on the subscription list - includes('ADMIN') would also match 'ADMIN_SUB'
const hasSubscription = (user, subscription) => {
    return (user?.subscriptions || '').split(',').map(type => type.trim()).includes(subscription);
};

const Header = ({ user, buttonsVisible, displayQiImage, reportId, pageTitle }) => {
    const navigate = useNavigate();
    const { isDemoMode, adminUser, enterDemoMode, exitDemoMode } = useContext(DemoModeContext);
    const [isOpen, setIsOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setIsSmallScreen(window.innerWidth < 1600); // screen width cutoff
        };

        handleResize(); // Initial check
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleLogout = async () => {
        navigate('/login');

        // in demo mode, log out the admin's own session
        const sessionUser = isDemoMode ? adminUser : user;
        if (isDemoMode) {
            exitDemoMode();
        }

        if (sessionUser) {
            const url = URLS.LOGOUT_URL
            const dict = {
                uid: sessionUser.uid,
                CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
            }

            await Network.post(url, dict, (response) => {
                const status = response.status
                if (Network.checkValidStatusAndResponse(response)) {
                    console.log("Logged Out Successfully")
                }
            })          
        }
    };

    const handleGoHome = async () => {
        navigate('/home'); 
        setIsOpen(false);
    };

    const handleGoQi = async () => {
        navigate('/qi'); 
        setIsOpen(false);
    };

    const handleGoMedicInfo = async () => {
        navigate('/medic-info'); 
        setIsOpen(false);
    };

    const handleGoAgencySettings = async () => {
        navigate('/agency-settings');
        setIsOpen(false);
    };

    const handleEnterDemoMode = async () => {
        setIsOpen(false);

        // the dev bypass-login User has no access token, so the server can't verify it as an admin
        if (!user.accessToken) {
            alert('Demo mode needs a real login - the bypass login has no access token.');
            return;
        }

        const url = URLS.ENTER_DEMO_MODE_URL
        const dict = {
            uid: user.uid,
            access_token: user.accessToken,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        }

        await Network.post(url, dict, (response) => {
            if (Network.checkValidStatusAndResponse(response)) {
                enterDemoMode(User.createFromJSON(response.data[0]));
                navigate('/home');
            } else if ([ErrorCodes.NOT_AUTHORIZED, ErrorCodes.INVALID_TOKEN].includes(String(response.data))) {
                alert('Demo mode is only available to admin users. Try logging out and back in.');
            } else if (String(response.data) === ErrorCodes.DEMO_MODE_UNAVAILABLE) {
                alert('Demo mode is not configured on the server (demo user missing or not linked only to the demo agency).');
            } else {
                alert('Could not enter demo mode - server error.');
            }
        })
    };

    const handleExitDemoMode = () => {
        setIsOpen(false);
        exitDemoMode();
        navigate('/home');
    };

    const handleGoDemoTest = async () => {
        navigate('/demo-test');
        setIsOpen(false);
    };

    const handleGoAgencyList = async () => {
        navigate('/agency-list');
        setIsOpen(false);
    };

    const handleGoUserList = async () => {
        navigate('/user-list');
        setIsOpen(false);
    };

    const handleGoToMedicView = () => {
        navigate(`/medic-report/${reportId}`);
        setIsOpen(false);
    };

    const handleGoToAdminView = () => {
        navigate(`/report/${reportId}`);
        setIsOpen(false);
    };

    return (
        <div className={(isSmallScreen ? "header-container" : "header-container-behind") + (isDemoMode ? " demo-mode" : "")}>
            {isDemoMode && (
                <div className="demo-mode-banner">
                    <span>DEMO MODE: all data shown is synthetic</span>
                    <button className="demo-mode-exit" onClick={handleExitDemoMode}>Exit Demo Mode</button>
                </div>
            )}
            <header className={isSmallScreen ? "header-screen-small" : "header"}>
                <div className="logo-container" onClick={handleGoHome} style={{ cursor: 'pointer' }}>
                    {displayQiImage ? 
                        <img src={require('../../images/logo-dark-QI.png')} alt="Logo" className="logo"/> :
                        <img src={require('../../images/logo-dark-QA.png')} alt="Logo" className="logo"/>
                    }
                </div>
                <div className="page-title">
                    {pageTitle || 'No Page Title Given'}
                </div>
                <div className="header-buttons">
                    {buttonsVisible?.medicViewButton && (
                        <button className="go-button" onClick={handleGoToMedicView}>
                            Go to Medic View
                        </button>
                    )}
                    {buttonsVisible?.adminViewButton && (
                        <button className="go-button" onClick={handleGoToAdminView}>
                            Go to Admin View
                        </button>
                    )}
                </div>
                <div className="company-info">
                    <div className="company-name" style={{ fontSize: 28 }}>
                        {user?.agencyName || 'External User'}
                    </div>
                    <div className="nav-dropdown">
                        <button 
                            className="select-btn"
                            aria-label="navigation menu"
                            aria-haspopup="listbox"
                            aria-expanded={isOpen}
                            aria-controls="nav-dropdown"
                            onClick={() => setIsOpen(!isOpen)}
                        >
                            <i className="fa-solid fa-angle-down"></i>
                        </button>
                        <ul className={isOpen ? "options-box open" : "options-box"} role="listbox">
                            {buttonsVisible?.homeButton && (
                                <li className="option" role="option" onClick={handleGoHome}>
                                    <i className="fa-solid fa-house" style={{ marginRight: '8px' }}></i> Home
                                </li>
                            )}
                            {buttonsVisible?.agencySettingsButton && (
                                <li className="option" role="option" onClick={handleGoAgencySettings}>
                                    <i className="fa-solid fa-gear" style={{ marginRight: '8px' }}></i> Processing Settings
                                </li>
                            )}
                            {buttonsVisible?.medicInfoButton && (
                                <li className="option" role="option" onClick={handleGoMedicInfo}>
                                    <i className="fa-solid fa-user-md" style={{ marginRight: '8px' }}></i> Medic Management
                                </li>
                            )}
                            {buttonsVisible?.qiButton && (
                                <li className="option" role="option" onClick={handleGoQi}>
                                    <i className="fa-solid fa-chart-line" style={{ marginRight: '8px' }}></i> Quality Improvement
                                </li>
                            )}
                            {buttonsVisible?.agencyListButton && (
                                <li className="option admin new-section" role="option" onClick={handleGoAgencyList}>
                                    <i className="fa-solid fa-building" style={{ marginRight: '8px' }}></i> Agency Management
                                </li>
                            )}
                            {buttonsVisible?.userListButton && (
                                <li className="option admin" role="option" onClick={handleGoUserList}>
                                    <i className="fa-solid fa-users-gear" style={{ marginRight: '8px' }}></i> User Management
                                </li>
                            )}
                            {!isDemoMode && hasSubscription(user, 'ADMIN') && (
                                <li className="option admin new-section" role="option" onClick={handleEnterDemoMode}>
                                    <i className="fa-solid fa-display" style={{ marginRight: '8px' }}></i> Enter Demo Mode
                                </li>
                            )}
                            {isDemoMode && (
                                <li className="option admin new-section" role="option" onClick={handleExitDemoMode}>
                                    <i className="fa-solid fa-display" style={{ marginRight: '8px' }}></i> Exit Demo Mode
                                </li>
                            )}
                            {user && (
                                <li className="option logout new-section" role="option" onClick={handleLogout}>
                                    <i className="fa-solid fa-right-from-bracket" style={{ marginRight: '8px' }}></i> Logout
                                </li>
                            )}
                        </ul>
                    </div>
                </div>
            </header>
        </div>
    );
};

export default Header;